/* eslint-disable indent */
/** common validators */

import {UseFormMethods, Validate} from 'react-hook-form';
import {ValidatorWithMethods} from './types';

/** Combine multiple validators into one */
export const combineValidators =
	<F,>(validators: ValidatorWithMethods<F>[]): ValidatorWithMethods<F> =>
	(value, context) => {
		for (let i = 0; i < validators.length; i++) {
			const result = validators[i](value, context);
			if (result) return result; // Return the first error message encountered
		}
		return null;
	};

/** generate a Validate method for use in the register function of react hook form that has access to the whole form values */
export const computeValidationWithContext =
	<F,>(methods: UseFormMethods, validator: ValidatorWithMethods<F>): Validate =>
	value =>
		validator ? validator(value, {getValues: methods.getValues as any}) : null;

/**
 * required ensure data is present.
 *
 * @param message The message to display.
 * @returns The message or `null`.
 */
export const required =
	<F,>(message = 'genericInputMessage.required'): ValidatorWithMethods<F> =>
	(value, {getValues}) => {
		return value && value !== '' ? null : message;
	};

/**
 * matched ensure password and reconfirm password is matched.
 *
 * @param message The message to display.
 * @returns The message or `undefined`.
 */
export const matched =
	<F,>(
		message = 'Password does not matched' // TODO: should be message path of the translation module
	): ValidatorWithMethods<F> =>
	(value, {getValues}) => {
		return value === (getValues() as any).password ? undefined : message;
	};

/**
 *Checks that the value is a string that contains a number.
 *
 * @param message The message to display
 * @returns The message or `undefined`.
 */
export const mustBeNumberString =
	<F,>(
		message = 'You should provide a decimal form string' // TODO: should be message path of the translation module
	): ValidatorWithMethods<F> =>
	value => {
		// regular expression should not be global (error during validation)
		const _regFloat = /^[-]?\d+(?:(?:[.,])?\d*)?$/g;
		const _result = _regFloat.test(value) ? undefined : message;
		// console.log('value to validate', value, 'result', _result);
		return _result;
	};

/**
 * Ensures the value is not negative.
 *
 * @param message The message to display.
 * @returns The message or `null`.
 */
export const mustNotBeNegative =
	<F,>(
		message = 'The value must not be negative' // TODO: should be message path of the translation module
	): ValidatorWithMethods<F> =>
	value => {
		return value >= 0 ? null : message;
	};

/**
 * Ensures the value is greater than or equal to zero.
 *
 * @param message The message to display.
 * @returns The message or `null`.
 */
export const equalOrOver0 =
	<F,>(message = 'genericInputMessage.equalOrOver0'): ValidatorWithMethods<F> =>
	value => {
		return value >= 0 ? null : message;
	};
